import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  useAllSubscriptions, usePauseSubscription, useDeactivateSubscription,
  describeSchedule, type SubscriptionWithJoins,
} from "@/lib/subscriptions";
import { PAYMENT_LABEL } from "@/lib/orders";

const fmt = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
const dfmt = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

export default function AbosPage() {
  const { data: subs = [], isLoading } = useAllSubscriptions();
  const [filter, setFilter] = useState<"all" | "active" | "paused">("active");
  const [running, setRunning] = useState(false);
  const [matResult, setMatResult] = useState<string | null>(null);

  const filtered = subs.filter((s) => {
    if (filter === "active") return s.is_active;
    if (filter === "paused") {
      if (!s.paused_until) return false;
      return new Date(s.paused_until) >= new Date();
    }
    return true;
  });

  async function runMaterialize() {
    setRunning(true);
    setMatResult(null);
    try {
      const { data } = await supabase.auth.getSession();
      const res = await fetch("/api/cron/abo-materialize", {
        method: "POST",
        headers: { Authorization: `Bearer ${data.session?.access_token ?? ""}` },
      });
      setMatResult(JSON.stringify(await res.json(), null, 2));
    } catch (e) {
      setMatResult(JSON.stringify({ error: (e as Error).message }, null, 2));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-bold text-hof-900">Abos</h1>
        <button onClick={runMaterialize} disabled={running} className="btn-secondary">
          {running ? "Läuft…" : "▶ Materialisierung jetzt ausführen"}
        </button>
      </div>

      {matResult && (
        <div className="card text-sm">
          <div className="font-medium mb-1">Materialisierungs-Ergebnis:</div>
          <pre className="overflow-x-auto text-xs bg-hof-50 p-2 rounded">{matResult}</pre>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <FilterBtn label="Aktive"     val="active" cur={filter} set={setFilter} />
        <FilterBtn label="Pausiert"   val="paused" cur={filter} set={setFilter} />
        <FilterBtn label="Alle"       val="all"    cur={filter} set={setFilter} />
      </div>

      {isLoading ? <div className="card">Lade…</div>
        : filtered.length === 0 ? <div className="card text-center text-hof-600">Keine Abos.</div>
        : (
        <div className="space-y-2">
          {filtered.map((s) => <AdminAboCard key={s.id} s={s} />)}
        </div>
      )}
    </div>
  );
}

function FilterBtn({ label, val, cur, set }: { label: string; val: "all"|"active"|"paused"; cur: string; set: (v: "all"|"active"|"paused") => void }) {
  return (
    <button onClick={() => set(val)}
      className={`px-3 py-1.5 rounded-full text-sm border ${
        cur === val ? "border-wiese-500 bg-wiese-50 text-wiese-800" : "border-hof-200 text-hof-600 hover:bg-hof-50"
      }`}>
      {label}
    </button>
  );
}

function AdminAboCard({ s }: { s: SubscriptionWithJoins }) {
  const pause = usePauseSubscription();
  const deactivate = useDeactivateSubscription();
  const isPaused = !!s.paused_until && new Date(s.paused_until) >= new Date();

  const productIds = s.items.map((i) => i.product_id);
  const { data: productMap = new Map() } = useQuery({
    queryKey: ["product-names", productIds.join(",")],
    enabled: productIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase.from("products").select("id,name,price_cents").in("id", productIds);
      return new Map((data ?? []).map((p) => [p.id, p]));
    },
  });
  const subtotal = s.items.reduce((acc, i) => acc + (productMap.get(i.product_id)?.price_cents ?? 0) * i.qty, 0);

  return (
    <article className="card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-hof-900">{s.customer?.display_name ?? "—"}</div>
          <div className="text-hof-700">{describeSchedule(s)}</div>
          {s.address && <div className="text-sm text-hof-500">{s.address.zip} {s.address.city}</div>}
          {isPaused && (
            <span className="pill-warn mt-1 inline-block">Pausiert bis {dfmt.format(new Date(s.paused_until!))}</span>
          )}
          {!s.is_active && <span className="pill bg-hof-200 text-hof-700 mt-1 inline-block">Beendet</span>}
          <div className="text-xs text-hof-600 mt-1">
            {s.items.map((i) => `${i.qty}× ${productMap.get(i.product_id)?.name ?? "—"}`).join(" · ")}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-hof-500">je Lieferung</div>
          <div className="text-lg font-bold text-wiese-700">{fmt.format(subtotal / 100)}</div>
          <div className="text-xs text-hof-500 mt-1">{PAYMENT_LABEL[s.payment_method]}</div>
        </div>
      </div>
      {s.is_active && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-hof-100">
          {isPaused ? (
            <button onClick={() => pause.mutate({ id: s.id, from: null, until: null })} className="btn-secondary text-sm">
              Pause aufheben
            </button>
          ) : (
            <button onClick={() => {
              const until = prompt("Pause bis (YYYY-MM-DD)?");
              if (!until) return;
              const from = new Date().toISOString().slice(0, 10);
              pause.mutate({ id: s.id, from, until });
            }} className="btn-secondary text-sm">Pausieren</button>
          )}
          <button onClick={() => {
            if (confirm(`Abo von ${s.customer?.display_name} wirklich beenden?`)) deactivate.mutate(s.id);
          }} className="btn-danger text-sm ml-auto">Beenden</button>
        </div>
      )}
    </article>
  );
}
